import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Truck, Mail, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GradeBadge } from "@/components/GradeBadge";
import { TrustBand } from "@/components/TrustBand";
import { EASE_OUT } from "@/components/motion/Reveal";
import { getOrder } from "@/lib/api";
import { formatPrice } from "@/lib/format";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getOrder(orderNumber).then(setOrder).catch(() => setError(true));
  }, [orderNumber]);

  if (error) {
    return (
      <div className="container-page section" data-testid="error-state">
        <div className="eyebrow">Ordine</div>
        <h1 className="display-lg mt-4">Ordine non trovato.</h1>
        <Button asChild variant="outline" className="mt-8"><Link to="/">Torna alla home</Link></Button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container-page section" data-testid="loading-skeleton">
        <div className="h-3 w-24 animate-pulse rounded bg-muted" />
        <div className="mt-6 h-14 w-2/3 animate-pulse rounded bg-muted" />
        <div className="mt-10 h-56 w-full max-w-2xl animate-pulse rounded bg-muted" />
      </div>
    );
  }

  return (
    <div className="container-page section" data-testid="order-confirmation">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
        <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE_OUT }}>
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-lime text-ink">
            <Check className="h-5 w-5" strokeWidth={2.5} />
          </div>
          <div className="eyebrow mt-8">Ordine confermato</div>
          <h1 className="display-xl mt-4 text-foreground">Grazie {order.customer.full_name.split(" ")[0]}, è tutto registrato.</h1>
          <p className="mt-5 max-w-xl text-lg text-muted-foreground">Abbiamo ricevuto il tuo ordine. Ti contatteremo a breve per finalizzare il pagamento in sicurezza tramite SumUp.</p>

          <div className="mt-10 inline-flex flex-col gap-1 rounded border border-border bg-card px-5 py-4" data-testid="order-number-box">
            <span className="mono-label text-muted-foreground">Numero ordine</span>
            <span className="font-mono text-2xl font-medium tracking-tight text-lime" data-testid="order-number">{order.order_number}</span>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex items-start gap-4 rounded border border-border p-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border"><Mail className="h-4 w-4 text-lime" strokeWidth={1.5} /></span>
              <div className="text-sm"><div className="font-medium text-foreground">Conferma via email</div><div className="mt-0.5 text-muted-foreground">A {order.customer.email}</div></div>
            </div>
            <div className="flex items-start gap-4 rounded border border-border p-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border"><Truck className="h-4 w-4 text-lime" strokeWidth={1.5} /></span>
              <div className="text-sm"><div className="font-medium text-foreground">Spedizione tracciata</div><div className="mt-0.5 text-muted-foreground">Consegna in 2-4 giorni</div></div>
            </div>
          </div>

          <Button asChild variant="lime" size="lg" className="mt-10" data-testid="order-continue-shopping">
            <Link to="/catalogo">Continua lo shopping <ArrowRight strokeWidth={2} /></Link>
          </Button>
        </motion.div>

        <motion.aside
          className="rounded border border-border bg-card p-6 lg:col-span-5 lg:self-start"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE_OUT, delay: 0.15 }}
          data-testid="order-summary"
        >
          <div className="mono-label text-muted-foreground">Riepilogo</div>
          <ul className="mt-5 divide-y divide-border">
            {order.items.map((item, i) => (
              <li key={i} className="flex items-start justify-between gap-4 py-3.5 text-sm">
                <div>
                  <div className="font-medium text-foreground">{item.name}</div>
                  <div className="mt-1.5 flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-mono text-muted-foreground">
                    <span>{item.brand}</span>
                    <GradeBadge grade={item.grade} compact />
                    <span>×{item.quantity}</span>
                  </div>
                </div>
                <div className="whitespace-nowrap font-mono text-foreground">{formatPrice(item.unit_price * item.quantity)}</div>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex items-end justify-between border-t border-border pt-4">
            <span className="mono-label text-muted-foreground">Totale</span>
            <span className="font-mono text-2xl font-medium text-foreground" data-testid="order-total">{formatPrice(order.total)}</span>
          </div>
          <TrustBand className="mt-6" data-testid="order-trust-band" />
        </motion.aside>
      </div>
    </div>
  );
}
